// Permit extension (0020_permit_extension.sql): pure helpers shared by the permit page, the
// action panels, the board and the print/archive renderer. No React, no DOM, explicit .ts
// imports, so Deno (archive worker) and Node (tests, docs) load the same file.
//
// The server decides everything (who may ask, the allowance, the timing, the RS's
// separation); these helpers only read what the bundle says and pre-check the form so the
// person sees a problem before sending.
import type { ExtensionAllowance, ExtensionRequestView, PermitBundle } from './types.ts'

/** What the PA or PH signs when asking (server text, mirrored to show above the checkbox). */
export const EXTENSION_REQUEST_DECLARATION =
  'I ask the Responsible Supervisor to extend the authorised period of this permit to the new end shown, for the reason given. The scope, location, hazards and controls stay as authorised. Until the RS approves, the permit is valid only to its current authorised end.'

/** What the RS signs when approving. */
export const EXTENSION_APPROVAL_DECLARATION =
  'As Responsible Supervisor I extend the authorised period of this permit to the new end shown. I have checked that the work, its scope and its controls stay as authorised and that the extension is within the allowance. The extension does not allow work by itself: each working period still needs pre-issue, validation and PH acceptance. The Operations Manager approval of this permit carries over.'

/** What the RS signs when refusing. */
export const EXTENSION_REFUSAL_DECLARATION =
  'As Responsible Supervisor I refuse this extension request for the reason given. The permit stays valid only to its current authorised end; after that the work needs a new permit.'

/** What the requester signs when withdrawing. */
export const EXTENSION_WITHDRAWAL_DECLARATION =
  'I withdraw my extension request. The permit stays valid only to its current authorised end.'

/** The allowance used when the policy is not loaded yet (SYN-0.3 class_b.extension_allowance_days). Display only. */
export const FALLBACK_EXTENSION_ALLOWANCE_DAYS = 7

const DAY_MS = 24 * 3_600_000

type Auth = PermitBundle['authorisations'][number]
type ExtBundle = Pick<PermitBundle, 'authorisations' | 'extension_requests' | 'extension_allowance'> & {
  permit?: Pick<PermitBundle['permit'], 'current_authorisation_id'> | null
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? null : t
}

function isoOf(t: number): string {
  return new Date(t).toISOString()
}

/**
 * The authorisation and every authorisation it extends, newest first. Follows
 * supersedes_authorisation_id from 'extension' rows only, as the server does.
 */
export function authorisationChain(b: Pick<PermitBundle, 'authorisations'>, authorisationId: string | null | undefined): Auth[] {
  const byId = new Map((b.authorisations ?? []).map((a) => [a.id, a]))
  const out: Auth[] = []
  const seen = new Set<string>()
  let cur = authorisationId ? byId.get(authorisationId) : undefined
  while (cur && !seen.has(cur.id)) {
    out.push(cur)
    seen.add(cur.id)
    if (cur.authorisation_kind !== 'extension' || !cur.supersedes_authorisation_id) break
    cur = byId.get(cur.supersedes_authorisation_id)
  }
  return out
}

export function currentAuthorisationOf(b: ExtBundle): Auth | null {
  const id = b.permit?.current_authorisation_id ?? null
  return (b.authorisations ?? []).find((a) => a.id === id) ?? null
}

/** The permit's initial authorisation (the end the allowance counts from). */
export function originalAuthorisation(b: ExtBundle): Auth | null {
  const chain = authorisationChain(b, b.permit?.current_authorisation_id)
  return chain.length > 0 ? chain[chain.length - 1] : null
}

export function extensionRequests(b: Pick<PermitBundle, 'extension_requests'>): ExtensionRequestView[] {
  return [...(b.extension_requests ?? [])]
}

/** The request waiting for the RS, if any. */
export function openExtensionRequest(b: Pick<PermitBundle, 'extension_requests'>): ExtensionRequestView | null {
  const open = extensionRequests(b).filter((r) => r.status === 'pending')
  return open.length > 0 ? open[open.length - 1] : null
}

export type ExtensionSummary = {
  originalTo: string
  currentTo: string
  /** null when the policy value is not usable (every request is refused). */
  allowanceDays: number | null
  latestAllowedTo: string | null
  /** How much of the allowance is used and left, in ms (0 when unknown). */
  usedMs: number
  remainingMs: number
  extended: boolean
  open: ExtensionRequestView | null
}

/**
 * Where the permit stands against its extension allowance. Prefers the server's
 * extension_allowance; on an older bundle it is worked out from the authorisations and
 * fallbackDays. Null before authorisation.
 */
export function extensionSummary(b: ExtBundle, fallbackDays: number | null = null): ExtensionSummary | null {
  const current = currentAuthorisationOf(b)
  const original = originalAuthorisation(b)
  if (!current || !original) return null
  const server: ExtensionAllowance | null | undefined = b.extension_allowance
  const originalTo = server?.original_authorisation_to ?? original.authorisation_to
  const currentTo = current.authorisation_to
  const allowanceDays = server ? server.allowance_days : fallbackDays
  const latest = server
    ? server.latest_allowed_to
    : allowanceDays !== null && ms(originalTo) !== null
      ? isoOf((ms(originalTo) as number) + allowanceDays * DAY_MS)
      : null
  const o = ms(originalTo) ?? 0
  const c = ms(currentTo) ?? o
  const l = ms(latest)
  return {
    originalTo,
    currentTo,
    allowanceDays,
    latestAllowedTo: latest,
    usedMs: Math.max(0, c - o),
    remainingMs: l === null ? 0 : Math.max(0, l - c),
    extended: current.authorisation_kind === 'extension',
    open: openExtensionRequest(b),
  }
}

/** "3 days", "1 day 6 hours", "5 hours", "0 hours". */
export function durationText(msValue: number): string {
  const hours = Math.floor(Math.max(0, msValue) / 3_600_000)
  const days = Math.floor(hours / 24)
  const rest = hours % 24
  const d = days === 1 ? '1 day' : `${days} days`
  const h = rest === 1 ? '1 hour' : `${rest} hours`
  if (days === 0) return h
  return rest === 0 ? d : `${d} ${h}`
}

/**
 * Problems with a proposed new end, the way the server checks it (EXTENSION_TOO_LATE,
 * EXTENSION_NOT_LATER, EXTENSION_OVER_ALLOWANCE), so the form says so before sending.
 * `fmt` formats a time for the message.
 */
export function proposedEndProblems(
  summary: ExtensionSummary | null,
  newEndIso: string | null,
  nowMs: number,
  fmt: (iso: string) => string,
): string[] {
  if (!summary) return ['This permit has no current authorisation to extend.']
  const out: string[] = []
  const current = ms(summary.currentTo)
  if (current !== null && nowMs >= current) {
    out.push(`The authorised period ended at ${fmt(summary.currentTo)}. It can no longer be extended; the work needs a new permit.`)
    return out
  }
  if (summary.allowanceDays === null || summary.latestAllowedTo === null) {
    out.push('The extension allowance in this permit\'s rules is not usable, so no extension can be approved.')
  }
  const end = ms(newEndIso)
  if (end === null) {
    out.push('Enter the new end date and time.')
    return out
  }
  if (current !== null && end <= current) out.push(`The new end must be later than the current authorised end (${fmt(summary.currentTo)}).`)
  const latest = ms(summary.latestAllowedTo)
  if (latest !== null && end > latest) {
    out.push(
      `The new end is beyond the allowance: at most ${summary.allowanceDays} days in total after the original end (${fmt(summary.originalTo)}), so no later than ${fmt(summary.latestAllowedTo as string)}.`,
    )
  }
  return out
}

export const EXTENSION_STATUS_TEXT: Record<ExtensionRequestView['status'], { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  pending: { label: 'Waiting for the RS', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  refused: { label: 'Refused', tone: 'bad' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
  lapsed: { label: 'Lapsed (permit cancelled)', tone: 'neutral' },
}

/** One plain sentence for the permit page and the printed permit. */
export function extensionSentence(b: ExtBundle, fmt: (iso: string) => string): string | null {
  const s = extensionSummary(b)
  if (!s) return null
  const open = s.open ? ` An extension to ${fmt(s.open.requested_to)} is waiting for the Responsible Supervisor.` : ''
  if (s.extended) {
    return `Authorised to ${fmt(s.currentTo)}, extended by the Responsible Supervisor from the original end ${fmt(s.originalTo)}.${open}`
  }
  return `Authorised to ${fmt(s.currentTo)} (no extension).${open}`
}
